import React, { useState } from "react";
import { api } from "../lib/api";
import { UploadCloud, Image as ImageIcon, Video, X } from "lucide-react";
import { toast } from "sonner";

export default function MediaUpload({ value = [], onChange, folder = "inspections" }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files);
    setUploading(true);
    const uploaded = [];
    for (const file of list) {
      const isVideo = file.type.startsWith("video");
      const resourceType = isVideo ? "video" : "image";
      try {
        const sigRes = await api.get("/cloudinary/signature", {
          params: { resource_type: resourceType, folder },
        });
        const sig = sigRes.data;
        const form = new FormData();
        form.append("file", file);
        form.append("folder", sig.folder);
        if (sig.has_api_key && sig.signature) {
          form.append("api_key", sig.api_key);
          form.append("timestamp", sig.timestamp);
          form.append("signature", sig.signature);
        } else if (sig.upload_preset) {
          // unsigned upload (preset must be configured in Cloudinary)
          form.append("upload_preset", sig.upload_preset);
        } else {
          toast.error("Cloudinary API Key yoxdur. Backend-də CLOUDINARY_API_KEY əlavə edin.");
          continue;
        }
        const url = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`;
        const res = await fetch(url, { method: "POST", body: form });
        const json = await res.json();
        if (json.secure_url) {
          uploaded.push({
            url: json.secure_url,
            public_id: json.public_id,
            type: resourceType,
            original: file.name,
          });
        } else {
          toast.error(json?.error?.message || "Yükləmə alınmadı");
        }
      } catch (e) {
        toast.error(e.message);
      }
    }
    setUploading(false);
    if (uploaded.length) onChange([...(value || []), ...uploaded]);
  };

  const removeAt = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div data-testid="media-upload">
      <label
        className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 bg-slate-50 rounded-lg p-8 cursor-pointer hover:border-[#004D99] hover:bg-blue-50 transition-colors"
        data-testid="media-dropzone"
      >
        <UploadCloud className="w-8 h-8 text-slate-400" aria-hidden="true" />
        <div className="text-sm font-medium">
          {uploading ? "Yüklənir..." : "Şəkil və ya video seçin / sürükləyin"}
        </div>
        <div className="text-xs text-slate-500 mono">JPG · PNG · MP4 · MOV</div>
        <input
          type="file"
          multiple
          accept="image/*,video/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="media-input"
        />
      </label>
      {value.length > 0 && (
        <div className="mt-4 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {value.map((m, i) => (
            <div
              key={i}
              className="relative group border border-slate-200 rounded-md overflow-hidden bg-slate-50"
              data-testid={`media-thumb-${i}`}
            >
              {m.type === "video" ? (
                <video src={m.url} className="w-full h-24 object-cover" />
              ) : (
                <img src={m.url} alt="" className="w-full h-24 object-cover" />
              )}
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 flex items-center gap-1">
                {m.type === "video" ? (
                  <Video className="w-3 h-3" />
                ) : (
                  <ImageIcon className="w-3 h-3" />
                )}
                <span className="truncate">{m.original}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
